/**
 * PlanChangeModal.jsx – Self-Service Plan-Wechsel & Add-on-Verwaltung
 *
 * Zwei-Schritt-Flow:
 *   Step 1 "configure": Paket / Interval / Add-ons auswählen → Preview laden
 *   Step 2 "preview":   Preisvergleich + ggf. Standort-Deaktivierung bestätigen
 *
 * PFLICHT-REGEL (User-Anforderung):
 *   target_location_slots wird IMMER auf die aktuelle aktive Standortanzahl
 *   des Kunden initialisiert, NIEMALS auf den Default 1.
 *   Verhindert "bitte Standort deaktivieren"-Fehler bei reinem Tier-Upgrade.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Loader2,
  Star,
  Briefcase,
  Zap,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Minus,
  Plus,
  MapPin,
  Users,
} from 'lucide-react';
import usePublicPackages from '../hooks/usePublicPackages';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Konstanten ────────────────────────────────────────────────────────────────

const PKG_META = {
  starter:         { label: 'Starter',         Icon: Star,      color: 'blue'   },
  business:        { label: 'Business',         Icon: Briefcase, color: 'indigo' },
  professional_ai: { label: 'Professional AI',  Icon: Zap,       color: 'violet' },
};

const PKG_ORDER = ['starter', 'business', 'professional_ai'];

const COLOR_CLASSES = {
  blue:   { ring: 'ring-blue-500',   selBg: 'bg-blue-50 dark:bg-blue-950/40',   iconBg: 'bg-blue-100 dark:bg-blue-900/30',   icon: 'text-blue-600',   badge: 'bg-blue-600' },
  indigo: { ring: 'ring-indigo-500', selBg: 'bg-indigo-50 dark:bg-indigo-950/40', iconBg: 'bg-indigo-100 dark:bg-indigo-900/30', icon: 'text-indigo-600', badge: 'bg-indigo-600' },
  violet: { ring: 'ring-violet-500', selBg: 'bg-violet-50 dark:bg-violet-950/40', iconBg: 'bg-violet-100 dark:bg-violet-900/30', icon: 'text-violet-600', badge: 'bg-violet-600' },
};

const ADDON_SLUGS = [
  { slug: 'employees_plus_5',  label: '+5 MA',  employees: 5  },
  { slug: 'employees_plus_10', label: '+10 MA', employees: 10 },
  { slug: 'employees_plus_20', label: '+20 MA', employees: 20 },
];

// ── Hilfsfunktion: Addon-Anzahl aus structured addons list lesen ──────────────
function addonQuantity(addons, slug) {
  const a = (addons || []).find(a => a.slug === slug && a.status !== 'inactive');
  return a ? (a.quantity || 1) : 0;
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────

export default function PlanChangeModal({ isOpen, onClose, subscription, onSuccess }) {
  const { packages } = usePublicPackages();

  // Aktuelle Config aus Subscription
  const currentCfg = subscription?.billing_v2_config || {};
  const currentPkg      = currentCfg.package || subscription?.billing_v2_package || 'starter';
  const currentInterval = currentCfg.billing_interval || 'month';
  const currentSlots    = currentCfg.booked_location_slots || 1;
  const currentAddons   = currentCfg.employee_addons || [];
  const isTrialing =
    subscription?.status === 'trialing' ||
    subscription?.stripe_status === 'trialing' ||
    subscription?.billing_status === 'trialing';

  // ── State ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState('configure'); // 'configure' | 'preview'

  // Konfiguration
  const [targetPkg,      setTargetPkg]      = useState(currentPkg);
  const [targetInterval, setTargetInterval] = useState(currentInterval);
  const [targetSlots,    setTargetSlots]    = useState(currentSlots); // WIRD bei Init überschrieben
  const [targetAddons,   setTargetAddons]   = useState(
    ADDON_SLUGS.map(a => ({ slug: a.slug, quantity: addonQuantity(currentAddons, a.slug) }))
  );

  // Init-Status: aktive Standortanzahl laden
  const [initLoading,            setInitLoading]            = useState(false);
  const [currentActiveLocations, setCurrentActiveLocations] = useState(currentSlots);

  // Preview
  const [preview,        setPreview]        = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);

  // Standort-Deaktivierung
  const [confirmationTexts, setConfirmationTexts] = useState({});
  const [locationsSelected, setLocationsSelected] = useState([]);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Reset beim Öffnen ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStep('configure');
      setTargetPkg(currentPkg);
      setTargetInterval(currentInterval);
      setPreview(null);
      setPreviewError(null);
      setSubmitError(null);
      setConfirmationTexts({});
      setLocationsSelected([]);
      setTargetAddons(
        ADDON_SLUGS.map(a => ({ slug: a.slug, quantity: addonQuantity(currentAddons, a.slug) }))
      );
      loadActiveLocationCount();
    }
  }, [isOpen]); // eslint-disable-line

  // PFLICHT-REGEL: aktive Standortanzahl laden → targetSlots initialisieren
  const loadActiveLocationCount = useCallback(async () => {
    setInitLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        target_package_slug: currentPkg,
        target_billing_interval: currentInterval,
        target_location_slots: String(currentSlots),
      });
      const res = await axios.get(`${API}/api/billing-v2/change-plan/preview?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const active = res.data?.validation?.current_active_locations ?? currentSlots;
      setCurrentActiveLocations(active);
      setTargetSlots(active); // ← NIEMALS Default 1, immer aktuelle Anzahl
    } catch {
      setTargetSlots(currentSlots); // Fallback: gebuchte Slots
    } finally {
      setInitLoading(false);
    }
  }, [currentPkg, currentInterval, currentSlots]);

  // ── Hilfsfunktionen: Preis aus Catalog ────────────────────────────────────
  const getNet = (slug) => {
    const pkg = packages?.[slug];
    if (!pkg) return null;
    const net = targetInterval === 'year' ? pkg.price_yearly_eur : pkg.price?.price_net_eur;
    return net ? +net : null;
  };
  // Backward-compat alias (war getGross)
  const getGross = getNet;

  // ── Add-on Menge ändern ───────────────────────────────────────────────────
  const setAddonQty = (slug, delta) => {
    setTargetAddons(prev => prev.map(a =>
      a.slug === slug ? { ...a, quantity: Math.max(0, Math.min(10, a.quantity + delta)) } : a
    ));
  };

  // ── Preview laden ──────────────────────────────────────────────────────────
  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const token = localStorage.getItem('token');
      const activeAddons = targetAddons.filter(a => a.quantity > 0);
      const params = new URLSearchParams({
        target_package_slug: targetPkg,
        target_billing_interval: targetInterval,
        target_location_slots: String(targetSlots),
        target_employee_addons: JSON.stringify(activeAddons),
      });
      const res = await axios.get(`${API}/api/billing-v2/change-plan/preview?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      setStep('preview');

      // Standort-Auswahl: alle aktiven Standorte vorselektieren bis zum benötigten Minimum
      const needed = res.data?.validation?.locations_to_deactivate_count || 0;
      if (needed > 0) {
        const locs = res.data?.validation?.locations_that_must_be_deactivated || [];
        setLocationsSelected(locs.slice(0, needed).map(l => l.id));
      }
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || e.message;
      setPreviewError(msg || 'Vorschau konnte nicht geladen werden.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Plan-Wechsel ausführen ────────────────────────────────────────────────
  const executeChange = async () => {
    if (!preview) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const activeAddons = targetAddons.filter(a => a.quantity > 0);
      const body = {
        target_package_slug:      targetPkg,
        target_billing_interval:  targetInterval,
        target_location_slots:    targetSlots,
        target_employee_addons:   activeAddons,
        locations_to_deactivate:  locationsSelected,
        confirmation_texts:       confirmationTexts,
      };
      await axios.post(`${API}/api/billing-v2/change-plan`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Paket erfolgreich zu ${PKG_META[targetPkg]?.label || targetPkg} gewechselt!`);
      onSuccess?.();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        (detail && typeof detail === 'object' ? detail.message : null) ||
        (typeof detail === 'string' ? detail : null) ||
        e.message ||
        'Fehler beim Plan-Wechsel. Bitte Support kontaktieren.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Berechnungen für Bestätigungsbutton ───────────────────────────────────
  const validation        = preview?.validation || {};
  const canChange         = validation.can_change;
  const neededToDeactivate = validation.locations_to_deactivate_count || 0;
  const availableLocs     = validation.locations_that_must_be_deactivated || [];

  const deactivationReady = neededToDeactivate === 0 || (
    locationsSelected.length >= neededToDeactivate &&
    locationsSelected.every(id => (confirmationTexts[id] || '').trim().length > 0)
  );

  const hasEmployeeError = validation.errors?.some(e => e.code === 'too_many_employees');
  const isNoop = targetPkg === currentPkg && targetInterval === currentInterval &&
    targetSlots === currentActiveLocations;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="plan-change-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {step === 'configure' ? 'Paket wechseln' : 'Wechsel bestätigen'}
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1: Konfiguration */}
        {step === 'configure' && (
          <div className="space-y-5">

            {initLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Aktuelle Konfiguration wird geladen…
              </div>
            )}

            {/* Interval-Toggle */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">Abrechnung:</span>
              <div className="flex rounded-lg border overflow-hidden">
                {[['month','Monatlich'],['year','Jährlich']].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setTargetInterval(val)}
                    data-testid={`interval-${val}`}
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                      targetInterval === val
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-muted text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Paket-Karten */}
            <div className="grid grid-cols-3 gap-3">
              {PKG_ORDER.map(slug => {
                const meta    = PKG_META[slug];
                const colors  = COLOR_CLASSES[meta.color];
                const gross   = getGross(slug);
                const isSelected  = targetPkg === slug;
                const isCurrent   = currentPkg === slug;
                return (
                  <button
                    key={slug}
                    onClick={() => setTargetPkg(slug)}
                    data-testid={`pkg-card-${slug}`}
                    className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                      isSelected
                        ? `${colors.ring} ring-2 ${colors.selBg} border-transparent`
                        : 'border-border hover:border-muted-foreground/40 bg-background'
                    }`}
                  >
                    {isCurrent && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full whitespace-nowrap">
                        Aktuell
                      </span>
                    )}
                    <div className={`p-2 rounded-lg ${colors.iconBg}`}>
                      <meta.Icon className={`h-5 w-5 ${colors.icon}`} />
                    </div>
                    <span className="font-semibold text-sm">{meta.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {gross != null ? `${gross.toFixed(2)} €` : '…'}
                      /{targetInterval === 'year' ? 'Jahr' : 'Monat'}
                    </span>
                    <p className="text-xs text-muted-foreground/70">zzgl. 19% MwSt.</p>
                    {isSelected && (
                      <CheckCircle2 className={`h-4 w-4 ${colors.icon} absolute bottom-2 right-2`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Standort-Slots (nur anzeigen wenn Kunde mehr als 1 aktiven Standort hat) */}
            {currentActiveLocations > 1 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Standortplätze</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTargetSlots(s => Math.max(1, s - 1))}
                    disabled={targetSlots <= 1}
                    className="p-1 rounded-md hover:bg-background border disabled:opacity-40"
                    data-testid="slots-decrement"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center font-bold" data-testid="slots-value">{targetSlots}</span>
                  <button
                    onClick={() => setTargetSlots(s => s + 1)}
                    className="p-1 rounded-md hover:bg-background border"
                    data-testid="slots-increment"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Mitarbeiter-Kontingent Info (statt kostenpflichtiger Add-on-Auswahl) */}
            {/* Add-ons (+5/+10/+20 MA) sind im employee_limit_reached-Dialog verfügbar, */}
            {/* nicht hier — denn jeder Standort bringt automatisch 15 MA kostenlos mit. */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">
                  {targetSlots} Standort{targetSlots !== 1 ? 'e' : ''} = <span className="text-primary font-bold">{targetSlots * 15} Mitarbeiterplätze</span> kostenlos inklusive
                </p>
                <p className="text-xs text-muted-foreground">
                  Jeder Standort bringt 15 MA-Plätze mit · Mehr MA-Plätze? Im Abo-Bereich Add-ons buchen
                </p>
              </div>
            </div>

            {previewError && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                {previewError}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Preview + ggf. Standort-Deaktivierung */}
        {step === 'preview' && preview && (
          <div className="space-y-4">

            {/* Trialing-Hinweis */}
            {(isTrialing || preview.is_trialing) && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg text-sm text-blue-800 dark:text-blue-200">
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  {preview.trial_ends_at ? (
                    <>
                      Ihre Testphase endet am{' '}
                      <strong>{new Date(preview.trial_ends_at).toLocaleDateString('de-DE')}</strong>.
                      Ab diesem Datum: <strong>{(preview.proposed?.total_gross / 1.19).toFixed(2)} €/Monat</strong>
                      <span className="font-normal text-xs"> zzgl. 19% MwSt.</span>
                    </>
                  ) : (
                    <>
                      Sie befinden sich in der kostenlosen Testphase.
                      Nach Ablauf: <strong>{(preview.proposed?.total_gross / 1.19).toFixed(2)} €/Monat</strong>
                      <span className="font-normal text-xs"> zzgl. 19% MwSt.</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Preisvergleich */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Aktuell</p>
                <p className="font-bold text-sm">{PKG_META[preview.current?.package_slug]?.label || preview.current?.package_slug}</p>
                <p className="text-sm text-muted-foreground">{(preview.current?.total_gross / 1.19).toFixed(2)} €</p>
                <p className="text-xs text-muted-foreground/70">zzgl. MwSt. / {preview.current?.billing_interval === 'year' ? 'Jahr' : 'Monat'}</p>
              </div>
              <div className={`p-3 rounded-lg border-2 ${
                (preview.is_upgrade ?? true) ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
              }`}>
                <p className="text-xs text-muted-foreground mb-1">Neu</p>
                <p className="font-bold text-sm">{PKG_META[preview.proposed?.package_slug]?.label || preview.proposed?.package_slug}</p>
                <p className="text-sm font-semibold">{(preview.proposed?.total_gross / 1.19).toFixed(2)} €</p>
                <p className="text-xs text-muted-foreground/70">zzgl. MwSt. / {targetInterval === 'year' ? 'Jahr' : 'Monat'}</p>
              </div>
            </div>

            {/* Preisdifferenz */}
              {preview.price_difference_gross != null && (
              <div className={`text-sm font-medium text-center p-2 rounded-lg ${
                preview.is_upgrade
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                  : 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200'
              }`}>
                {preview.is_upgrade
                  ? `Mehrkosten: +${(Math.abs(preview.price_difference_gross) / 1.19).toFixed(2)} €/Monat`
                  : `Ersparnis: -${(Math.abs(preview.price_difference_gross) / 1.19).toFixed(2)} €/Monat`
                }
                <span className="text-xs font-normal ml-1">zzgl. 19% MwSt.</span>
              </div>
            )}

            {/* ── Checkout-Aufschlüsselung (finaler Bestätigungsschritt) ── */}
            {canChange && preview.proposed?.total_gross && !isTrialing && (
              <div className="rounded-xl border-2 border-primary/20 bg-muted/20 p-4 space-y-1.5" data-testid="checkout-summary">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Gesamtbetrag ab nächster Abrechnung</p>
                <div className="flex justify-between text-sm">
                  <span>Netto</span>
                  <span className="font-medium">{(preview.proposed.total_gross / 1.19).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>MwSt. 19%</span>
                  <span>{(preview.proposed.total_gross - preview.proposed.total_gross / 1.19).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t pt-1.5 mt-1">
                  <span>Gesamt (Brutto)</span>
                  <span className="text-primary">{preview.proposed.total_gross.toFixed(2)} € / {targetInterval === 'year' ? 'Jahr' : 'Monat'}</span>
                </div>
              </div>
            )}
            {canChange && preview.proposed?.total_gross && isTrialing && (
              <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-1.5" data-testid="checkout-summary-trial">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2">Betrag nach Ende der Testphase</p>
                <div className="flex justify-between text-sm">
                  <span>Netto</span>
                  <span className="font-medium">{(preview.proposed.total_gross / 1.19).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>MwSt. 19%</span>
                  <span>{(preview.proposed.total_gross - preview.proposed.total_gross / 1.19).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t pt-1.5 mt-1">
                  <span>Gesamt (Brutto)</span>
                  <span className="text-blue-700 dark:text-blue-300">{preview.proposed.total_gross.toFixed(2)} € / {targetInterval === 'year' ? 'Jahr' : 'Monat'}</span>
                </div>
              </div>
            )}

            {/* Stripe Proration Hinweis (nur aktive, nicht trialing) */}
            {!isTrialing && preview.stripe_preview?.source === 'stripe_preview' && (
              <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded-lg">
                Sofortige Anteilsabrechnung (Proration): <strong>{preview.stripe_preview?.amount_due_eur?.toFixed(2)} €</strong> werden mit der nächsten Rechnung verrechnet.
              </div>
            )}

            {!isTrialing && (
              <div className="text-xs text-muted-foreground p-2 border rounded-lg">
                Unterschiede werden bei der nächsten Abrechnung anteilig verrechnet (proration_behavior: create_prorations).
              </div>
            )}

            {/* Validierungsfehler */}
            {!canChange && validation.errors?.map((err, i) => (
              <div key={i} className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                {err.message}
              </div>
            ))}

            {/* ── Submit-Fehler (inline, immer sichtbar) ── */}
            {submitError && (
              <div
                className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl text-sm text-red-800 dark:text-red-200"
                data-testid="plan-change-submit-error"
              >
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-red-600" />
                <div>
                  <p className="font-semibold mb-1">Fehler beim Plan-Wechsel</p>
                  <p>{submitError}</p>
                </div>
              </div>
            )}

            {/* Standort-Deaktivierung (nur wenn Downgrade Standorte reduziert) */}
            {neededToDeactivate > 0 && (
              <div className="space-y-3 p-4 border-2 border-orange-300 dark:border-orange-700 rounded-xl bg-orange-50 dark:bg-orange-900/20">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                    Bitte wählen Sie {neededToDeactivate} Standort{neededToDeactivate > 1 ? 'e' : ''} zur Deaktivierung aus und bestätigen Sie durch Eingabe des Standortnamens.
                  </p>
                </div>

                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {availableLocs.map(loc => {
                    const isChecked = locationsSelected.includes(loc.id);
                    return (
                      <div key={loc.id} className="bg-white dark:bg-gray-900 border rounded-lg p-3 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              if (e.target.checked) {
                                setLocationsSelected(prev => [...prev, loc.id]);
                              } else {
                                setLocationsSelected(prev => prev.filter(id => id !== loc.id));
                                setConfirmationTexts(prev => {
                                  const n = { ...prev };
                                  delete n[loc.id];
                                  return n;
                                });
                              }
                            }}
                            className="rounded"
                            data-testid={`loc-checkbox-${loc.id}`}
                          />
                          <span className="font-medium text-sm">{loc.name}</span>
                          {loc.city && <span className="text-xs text-muted-foreground">({loc.city})</span>}
                        </label>
                        {isChecked && (
                          <input
                            type="text"
                            placeholder={`Standortname bestätigen: "${loc.name}"`}
                            value={confirmationTexts[loc.id] || ''}
                            onChange={e =>
                              setConfirmationTexts(prev => ({ ...prev, [loc.id]: e.target.value }))
                            }
                            className="w-full px-3 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-orange-400"
                            data-testid={`loc-confirm-${loc.id}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-orange-700 dark:text-orange-300">
                  Die Daten (Aufgaben, Temperaturlogs, etc.) bleiben erhalten. Der Standort wird nur deaktiviert, nicht gelöscht.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer-Buttons */}
        <DialogFooter className="pt-2">
          {step === 'configure' && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="plan-change-cancel">
                Abbrechen
              </Button>
              <Button
                onClick={loadPreview}
                disabled={previewLoading || initLoading || isNoop}
                data-testid="plan-change-preview-btn"
              >
                {previewLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Wird geladen…</>
                ) : (
                  <><ArrowRight className="h-4 w-4 mr-2" />Vorschau anzeigen</>
                )}
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button
                variant="outline"
                onClick={() => { setStep('configure'); setPreview(null); setSubmitError(null); }}
                data-testid="plan-change-back"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Zurück
              </Button>
              <Button
                onClick={executeChange}
                disabled={
                  submitting ||
                  hasEmployeeError ||
                  (neededToDeactivate > 0 && !deactivationReady) ||
                  (!canChange && neededToDeactivate === 0)
                }
                data-testid="plan-change-confirm-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Wird ausgeführt…</>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Jetzt wechseln
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
